import React, { useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import { toast } from 'react-toastify';
import api from '../services/api';
import { handlePhoneInput, cleanPhoneNumber } from '../utils/phoneValidation';
import NationalitySelect from './NationalitySelect';
import './TeacherForm.css';

const TeacherForm = ({ teacher, onSuccess, onCancel }) => {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    full_name: '',
    phone: '',
    nationality_code: 'PE',
    password: '',
    role_ids: [],
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Obtener todos los roles disponibles
  const { data: availableRoles } = useQuery('roles', async () => {
    try {
      const response = await api.get('/users/roles');
      return response.data;
    } catch (error) {
      return [];
    }
  });

  // Obtener los IDs de los roles teacher y secretary
  const teacherRoleId = availableRoles?.find(r => r.name === 'teacher')?.id;
  const secretaryRoleId = availableRoles?.find(r => r.name === 'secretary')?.id;

  useEffect(() => {
    if (teacher) {
      setFormData({
        username: teacher.username || '',
        email: teacher.email || '',
        full_name: teacher.full_name || '',
        phone: teacher.phone || '',
        nationality_code: teacher.nationality_code || 'PE',
        password: '', // No mostrar contraseña existente
        role_ids: teacher.roles?.map(r => r.id) || [],
      });
    } else {
      // Si es nuevo, no asignar rol por defecto - el usuario debe elegir
      setFormData(prev => ({
        ...prev,
        role_ids: []
      }));
    }
  }, [teacher, teacherRoleId, secretaryRoleId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Limpiar error del campo
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validate = () => {
    const newErrors = {};
    
    if (!formData.username.trim()) {
      newErrors.username = 'El usuario es requerido';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'El email es requerido';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Email inválido';
    }
    
    if (!formData.full_name.trim()) {
      newErrors.full_name = 'El nombre completo es requerido';
    }
    
    if (!teacher && !formData.password) {
      newErrors.password = 'La contraseña es requerida para nuevos usuarios';
    }
    
    if (formData.password && formData.password.length < 6) {
      newErrors.password = 'La contraseña debe tener al menos 6 caracteres';
    }

    // Validar que tenga al menos un rol asignado
    if (!formData.role_ids || formData.role_ids.length === 0) {
      newErrors.role_ids = 'Debe asignar al menos un rol';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Validar que tenga al menos un rol (teacher o secretary)
      let roleIds = formData.role_ids || [];
      if (roleIds.length === 0) {
        setErrors({ role_ids: 'Debe seleccionar al menos un rol (Profesor o Secretario)' });
        setIsSubmitting(false);
        return;
      }
      
      // Validar que solo tenga roles permitidos (teacher o secretary)
      const allowedRoleIds = [teacherRoleId, secretaryRoleId].filter(Boolean);
      const invalidRoles = roleIds.filter(id => !allowedRoleIds.includes(id));
      if (invalidRoles.length > 0) {
        setErrors({ role_ids: 'Solo puede asignar roles de Profesor o Secretario' });
        setIsSubmitting(false);
        return;
      }

      const payload = {
        username: formData.username.trim(),
        email: formData.email.trim(),
        full_name: formData.full_name.trim(),
        phone: formData.phone ? cleanPhoneNumber(formData.phone.trim()) : null,
        nationality_code: formData.nationality_code || 'PE',
        role_ids: roleIds,
      };

      // Solo incluir password si se está creando o se cambió (y no está vacío)
      if (!teacher) {
        // Al crear, la contraseña es obligatoria
        payload.password = formData.password;
      } else if (formData.password && formData.password.trim() !== '') {
        // Al editar, solo incluir si se proporcionó una contraseña nueva
        payload.password = formData.password;
      }

      if (teacher) {
        await api.put(`/users/${teacher.id}`, payload);
      } else {
        await api.post('/users', payload);
      }

      onSuccess();
    } catch (error) {
      
      if (error.response?.data?.detail) {
        if (typeof error.response.data.detail === 'string') {
          toast.error(error.response.data.detail, { autoClose: 5000 });
          setErrors({ submit: error.response.data.detail });
        } else if (Array.isArray(error.response.data.detail)) {
          const fieldErrors = {};
          const messages = [];
          error.response.data.detail.forEach(err => {
            if (err.loc && err.loc.length > 1) {
              fieldErrors[err.loc[1]] = err.msg;
              messages.push(err.msg);
            }
          });
          setErrors(fieldErrors);
          if (messages.length > 0) {
            toast.error(messages.join('. '), { autoClose: 5000 });
          }
        }
      } else {
        const msg = error.message || 'Error al guardar el personal';
        toast.error(msg, { autoClose: 5000 });
        setErrors({ submit: msg });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="teacher-form">
      {errors.submit && (
        <div className="error-message">{errors.submit}</div>
      )}

      <div className="form-group">
        <label>
          Nombre Completo <span className="required">*</span>
        </label>
        <input
          type="text"
          name="full_name"
          value={formData.full_name}
          onChange={handleChange}
          className={errors.full_name ? 'error' : ''}
        />
        {errors.full_name && <span className="field-error">{errors.full_name}</span>}
      </div>

      <div className="form-group">
        <label>
          Email <span className="required">*</span>
        </label>
        <input
          type="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          className={errors.email ? 'error' : ''}
        />
        {errors.email && <span className="field-error">{errors.email}</span>}
      </div>

      <div className="form-group">
        <label>Teléfono</label>
        <input
          type="tel"
          name="phone"
          value={formData.phone}
          onChange={(e) => {
            handlePhoneInput(e);
            handleChange(e);
          }}
          placeholder="+51 900 000 000"
        />
      </div>

      <div className="form-group">
        <label>Nacionalidad</label>
        <NationalitySelect
          value={formData.nationality_code}
          onChange={(code) => handleChange({ target: { name: 'nationality_code', value: code } })}
        />
      </div>

      <div className="form-group">
        <label>
          Rol <span className="required">*</span>
        </label>
        <div className="role-selection">
          {teacherRoleId && (
            <label className="role-option">
              <input
                type="checkbox"
                checked={formData.role_ids.includes(teacherRoleId)}
                onChange={(e) => {
                  if (e.target.checked) {
                    setFormData(prev => ({
                      ...prev,
                      role_ids: [...prev.role_ids.filter(id => id !== teacherRoleId), teacherRoleId]
                    }));
                  } else {
                    setFormData(prev => ({
                      ...prev,
                      role_ids: prev.role_ids.filter(id => id !== teacherRoleId)
                    }));
                  }
                }}
              />
              <span>👨‍🏫 Profesor</span>
            </label>
          )}
          {secretaryRoleId && (
            <label className="role-option">
              <input
                type="checkbox"
                checked={formData.role_ids.includes(secretaryRoleId)}
                onChange={(e) => {
                  if (e.target.checked) {
                    setFormData(prev => ({
                      ...prev,
                      role_ids: [...prev.role_ids.filter(id => id !== secretaryRoleId), secretaryRoleId]
                    }));
                  } else {
                    setFormData(prev => ({
                      ...prev,
                      role_ids: prev.role_ids.filter(id => id !== secretaryRoleId)
                    }));
                  }
                }}
              />
              <span>📋 Secretario/a</span>
            </label>
          )}
        </div>
        {errors.role_ids && <span className="field-error">{errors.role_ids}</span>}
      </div>

      <div className="form-section-divider">
        <h4>Credenciales de Acceso</h4>
      </div>

      <div className="form-group">
        <label>
          Usuario <span className="required">*</span>
        </label>
        <input
          type="text"
          name="username"
          value={formData.username}
          onChange={handleChange}
          disabled={!!teacher}
          className={errors.username ? 'error' : ''}
          autoComplete="off"
        />
        {errors.username && <span className="field-error">{errors.username}</span>}
      </div>

      <div className="form-group">
        <label>
          Contraseña {!teacher && <span className="required">*</span>}
        </label>
        <div className="password-input-wrapper">
          <input
            type={showPassword ? 'text' : 'password'}
            name="password"
            value={formData.password}
            onChange={handleChange}
            placeholder={teacher ? "Dejar vacío para no cambiar" : "Mínimo 6 caracteres"}
            className={errors.password ? 'error' : ''}
            autoComplete="new-password"
          />
          <button
            type="button"
            className="password-toggle"
            onClick={() => setShowPassword(!showPassword)}
            aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
          >
            {showPassword ? '👁️' : '👁️‍🗨️'}
          </button>
        </div>
        {errors.password && <span className="field-error">{errors.password}</span>}
        {teacher && (
          <small className="form-hint">Dejar vacío si no deseas cambiar la contraseña</small>
        )}
      </div>

      <div className="form-actions">
        <button type="button" className="btn btn-secondary" onClick={onCancel}>
          Cancelar
        </button>
        <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
          {isSubmitting ? 'Guardando...' : teacher ? 'Actualizar' : 'Crear'}
        </button>
      </div>
    </form>
  );
};

export default TeacherForm;

